// A big, thumb-sized chip for one aroma family: the drawing, the name and a band of the family's
// colour along the foot. Chosen is a filled chip with a check, so it never depends on the colour;
// the accessible themes draw the band as a bordered swatch.
import "./config";
import { Text, View, styled } from "tamagui";
import { AromaIcon, type AromaIconId } from "./AromaIcon";
import { pressed } from "./aria";
import { Pressable } from "./controls";
import { Icon } from "./Icon";
import type { PaletteKey } from "./palette";

const Frame = styled(Pressable, {
  name: "FamilyChip",
  flexDirection: "row",
  gap: "2",
  minHeight: "var(--uiTarget)",
  paddingLeft: "3",
  paddingRight: "3",
  paddingTop: "2",
  paddingBottom: 10,
  borderRadius: "3",
  borderWidth: "var(--borderW)",
  borderColor: "line",
  backgroundColor: "surface",
  overflow: "hidden",
  variants: {
    active: {
      true: {
        backgroundColor: { default: "accentSoft", a11yLight: "accent", a11yDark: "accent" },
        borderColor: "accent",
      },
    },
  } as const,
});

const Label = styled(Text, {
  name: "FamilyChipLabel",
  fontFamily: "body",
  fontSize: "2",
  fontWeight: "700",
  color: "text",
  variants: {
    active: {
      true: { color: { default: "accent", a11yLight: "accentInk", a11yDark: "accentInk" } },
    },
  } as const,
});

export interface FamilyChipProps {
  name: string;
  icon: AromaIconId;
  colour: PaletteKey;
  active: boolean;
  /** Not choosable now (the cap is reached); a chosen family can always be unchosen. */
  disabled?: boolean | undefined;
  onPress: () => void;
  testId?: string | undefined;
}

export function FamilyChip({
  name,
  icon,
  colour,
  active,
  disabled = false,
  onPress,
  testId,
}: FamilyChipProps): React.JSX.Element {
  return (
    <Frame
      active={active}
      disabled={disabled && !active}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...pressed(active)}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(testId === undefined ? {} : { "data-testid": testId })}
      onPress={onPress}
    >
      <AromaIcon id={icon} size={28} />
      <Label active={active}>{name}</Label>
      {active ? <Icon name="check" size={14} tone="accent" /> : null}
      <View
        aria-hidden
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        height={{ default: 5, a11yLight: 8, a11yDark: 8 }}
        borderTopWidth={{ default: 0, a11yLight: 2, a11yDark: 2 }}
        borderColor="text"
        backgroundColor={colour}
      />
    </Frame>
  );
}
